import React, { useState, useEffect } from 'react'
import './ProjectDetailPage.css'
import { useSelector, useDispatch } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import MenuBar from "../components/MenuBar"
import Header from "../components/Header"
import LoadingSpinner from '../components/placeholders/LoadingSpinner'
import httpClient from '../components/helpers/httpClient'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import CircularProgress from '@mui/material/CircularProgress'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DownloadIcon from '@mui/icons-material/Download'
import BuildingIcon from '../assets/pictos/svg/BuildingIcon.jsx'
import PopupWindowProjectDetail from '../components/PopupWindowProjectDetail'
import PopupWindowBuildingDetail from '../components/popup-window-building-detail/PopupWindowBuildingDetail'
import Sold from '../assets/pictos/svg/Sold.jsx'
import RentedIcon from '../assets/pictos/svg/RentedIcon.jsx'
import ExportButton from '../components/UI/ExportButton.jsx'
import ImportButton from '../components/UI/ImportButton.jsx'
import Button from '../components/UI/Button.jsx'
import Slider from '../components/Slider'
import { toast } from 'react-toastify'
import { showNotificationPopup } from '../components/NotificationPopup'
import jsPDF from 'jspdf'
import Logo from '../assets/logos/Logo-Vitruvi.png'

const toNumber = (value, fallback = 0) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

const formatMoney = (value) => {
  const number = toNumber(value)
  if (!number) return '-'

  return `€ ${number.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const formatArea = (value) => {
  if (value === null || value === undefined || value === '') return '-'

  return `${toNumber(value).toLocaleString('nl-BE', { maximumFractionDigits: 2 })} m2`
}

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-'

  return String(value)
}

const loadImage = async (src) => {
  const response = await fetch(src)
  const blob = await response.blob()

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

  const image = await new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = dataUrl
  })

  return {
    dataUrl,
    width: image.naturalWidth,
    height: image.naturalHeight,
  }
}

const statusLabels = {
  available: 'Beschikbaar',
  sold: 'Verkocht',
  rented: 'Verhuurd',
  inactive: 'Inactief',
  demolished: 'Gesloopt',
}

const typeLabels = {
  Apartment: 'Appartement',
  House: 'Woning',
  Parking: 'Parking',
  Office: 'Kantoor',
  Commercial: 'Commercieel',
  Warehouse: 'Magazijn',
  Storage: 'Opslag',
  'Student Room': 'Studentenkamer',
  Room: 'Kamer',
  Land: 'Grond',
  'Industrial Building': 'Industrieel gebouw',
  Other: 'Andere',
}

const calculateCommercialTotal = (prices = {}) => {
  const land = toNumber(prices.commercial_land_price)
  const construction = toNumber(prices.commercial_construction_price)
  const connection = toNumber(prices.commercial_connection_cost)
  const contract = toNumber(prices.commercial_contract_cost)
  const vatPercentage = toNumber(prices.commercial_vat_percentage)
  const constructionVatPercentage = toNumber(prices.commercial_construction_vat_percentage, vatPercentage)
  const connectionVatPercentage = toNumber(prices.commercial_connection_vat_percentage, vatPercentage)
  const contractVatPercentage = toNumber(prices.commercial_contract_vat_percentage, vatPercentage)
  const registrationPercentage = toNumber(prices.commercial_registration_percentage)

  return land + construction + connection + contract
    + (land * registrationPercentage)
    + (construction * constructionVatPercentage)
    + (connection * connectionVatPercentage)
    + (contract * contractVatPercentage)
}

function ProjectDetailPage() {
  const [project, setProject] = useState([])
  const [statuses, setStatuses] = useState([])
  const [edit, setEdit] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingStatus, setloadingStatus] = useState(false)
  const [popupIsActive, setPopupIsActive] = useState(false)
  const [actionType, setActionType] = useState("")
  const [selectedType, setSelectedType] = useState("")
  const [selectedItemID, setSelectedItemID] = useState(null)
  const [selectedItem, setSelectedItem] = useState({})
  const [tags, setTags] = useState([])
  const [notes, setNotes] = useState([])
  const [contactsList, setContactsList] = useState([{ "id": null, "first_name": null, "last_name": null }])
  const [projectDetailIsActive, setProjectDetailIsActive] = useState(false)
  const [buildingDetailIsActive, setBuildingDetailIsActive] = useState(false)
  const [showDeleteButton, setShowDeleteButton] = useState({ type: null, id: null })
  const [hideInactive, setHideInactive] = useState(false)
  const [exportingCommercialPdf, setExportingCommercialPdf] = useState(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { projectID } = useParams()

  const prepareExportData = async () => {
    if (!project || !project.id) return []

    // Collect all unit IDs
    const unitIds = []
    if (project.buildings && Array.isArray(project.buildings)) {
      project.buildings.forEach(building => {
        if (building.units && Array.isArray(building.units)) {
          building.units.forEach(unit => {
            unitIds.push(unit.id)
          })
        }
      })
    }

    // Fetch price details for all units in one API call
    let priceDetails = {}
    if (unitIds.length > 0) {
      try {
        const response = await httpClient.post(import.meta.env.VITE_API_URL + '/api/units/export', {
          unit_ids: unitIds
        })
        priceDetails = response.data
      } catch (error) {
        console.error('Failed to fetch price details:', error)
        // Continue without price details
      }
    }

    const exportData = []

    // Export only units with project and building context
    if (project.buildings && Array.isArray(project.buildings)) {
      project.buildings.forEach(building => {
        if (building.units && Array.isArray(building.units)) {
          building.units.forEach(unit => {
            const unitPrices = priceDetails[unit.id] || {}

            exportData.push({
              'Unit ID': unit.id,
              'Unit Name': unit.unit_name,
              'Unit Address': unit.address || '',
              'Unit Type': unit.type,
              'Unit Status': unit.status || 'available',
              'Habitable Area (m²)': unit.habitable_area_m2 ?? '',
              'Terrace Area (m²)': unit.terrace_area_m2 ?? '',
              'Garden Area (m²)': unit.garden_area_m2 ?? '',
              'Floor Level': unit.floor_level ?? '',
              'Building ID': building.id,
              'Building Name': building.building_name,
              'Project Name': project.project_commercial_name || project.project_placeholder_name,
              'Commercial Land Price': unitPrices.commercial_land_price,
              'Commercial Construction Price': unitPrices.commercial_construction_price,
              'Commercial Connection Cost': unitPrices.commercial_connection_cost,
              'Commercial Contract Cost': unitPrices.commercial_contract_cost,
              'Commercial VAT %': unitPrices.commercial_vat_percentage,
              'Commercial Registration %': unitPrices.commercial_registration_percentage,
            })
          })
        }
      })
    }

    // If no data, return at least the headers as a template
    if (exportData.length === 0) {
      exportData.push({
        'Unit ID': '',
        'Unit Name': '',
        'Unit Address': '',
        'Unit Type': '',
        'Unit Status': '',
        'Habitable Area (m²)': '',
        'Terrace Area (m²)': '',
        'Garden Area (m²)': '',
        'Floor Level': '',
        'Building ID': '',
        'Building Name': '',
        'Project Name': project.project_commercial_name || project.project_placeholder_name || '',
        'Commercial Land Price': '',
        'Commercial Construction Price': '',
        'Commercial Connection Cost': '',
        'Commercial Contract Cost': '',
        'Commercial VAT %': '',
        'Commercial Registration %': '',
      })
    }

    return exportData
  }

  const getProjectUnits = () => {
    if (!Array.isArray(project?.buildings)) return []

    return project.buildings.flatMap(building => {
      const sortedUnits = [...(building.units || [])].sort((a, b) => {
        const aUnitName = a.unit_name || ''
        const bUnitName = b.unit_name || ''
        const aMatch = aUnitName.match(/(\d+)$/)
        const bMatch = bUnitName.match(/(\d+)$/)

        if (aMatch && bMatch) {
          return parseInt(aMatch[1], 10) - parseInt(bMatch[1], 10)
        }

        return aUnitName.localeCompare(bUnitName)
      })

      return sortedUnits.map(unit => ({
        ...unit,
        building_name: building.building_name || '',
      }))
    })
  }

  const handleCommercialPdfExport = async () => {
    if (exportingCommercialPdf) return

    const units = getProjectUnits()
    if (units.length === 0) {
      toast.error('No units available for this project')
      return
    }

    setExportingCommercialPdf(true)

    try {
      const response = await httpClient.post(import.meta.env.VITE_API_URL + '/api/units/export', {
        unit_ids: units.map(unit => unit.id)
      })
      const priceDetails = response.data || {}
      const projectName = project.project_commercial_name || project.project_placeholder_name || 'Project'
      let logoImage = null
      try {
        logoImage = await loadImage(Logo)
      } catch (error) {
        console.warn('Failed to load logo for commercial PDF:', error)
      }
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 12
      const rowHeight = 8
      const colWidths = [28, 34, 28, 25, 18, 24, 22, 24, 25, 25, 26]
      const headers = ['Unit', 'Gebouw', 'Type', 'Status', 'Niveau', 'Bewoonb.', 'Terras', 'Tuin', 'Grond', 'Constructie', 'Totaal incl.']
      let y = 40

      const addHeader = () => {
        if (logoImage) {
          const maxLogoWidth = 34
          const maxLogoHeight = 14
          const ratio = Math.min(maxLogoWidth / logoImage.width, maxLogoHeight / logoImage.height)
          const logoWidth = logoImage.width * ratio
          const logoHeight = logoImage.height * ratio

          doc.addImage(
            logoImage.dataUrl,
            'PNG',
            pageWidth - margin - logoWidth,
            12,
            logoWidth,
            logoHeight
          )
        }

        doc.setFont('helvetica', 'bold')
        doc.setFontSize(16)
        doc.text(`Commerciele prijslijst - ${projectName}`, margin, 16)
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.text(`Gegenereerd op ${new Date().toLocaleDateString('nl-BE')}`, margin, 24)
        doc.text('Overzicht van units, beschikbaarheid, oppervlaktes en commerciele prijzen.', margin, 31)

        doc.setFillColor(245, 247, 249)
        doc.rect(margin, y - 6, pageWidth - (margin * 2), rowHeight, 'F')
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(8)
        let x = margin + 1
        headers.forEach((header, index) => {
          doc.text(header, x, y - 1)
          x += colWidths[index]
        })
        doc.setDrawColor(210, 210, 210)
        doc.line(margin, y + 2, pageWidth - margin, y + 2)
        y += rowHeight
      }

      const addFooter = () => {
        const pageCount = doc.internal.getNumberOfPages()
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i)
          doc.setFont('helvetica', 'normal')
          doc.setFontSize(8)
          doc.setTextColor(120, 120, 120)
          doc.text(`Pagina ${i} / ${pageCount}`, pageWidth - margin - 22, pageHeight - 8)
          doc.setTextColor(0, 0, 0)
        }
      }

      addHeader()

      units.forEach((unit, index) => {
        if (y > pageHeight - 18) {
          doc.addPage()
          y = 40
          addHeader()
        }

        const prices = priceDetails[unit.id] || priceDetails[String(unit.id)] || {}
        const status = unit.status?.toLowerCase() || 'available'
        const row = [
          formatValue(unit.unit_name),
          formatValue(unit.building_name),
          typeLabels[unit.type] || formatValue(unit.type),
          statusLabels[status] || formatValue(unit.status || 'available'),
          formatValue(unit.floor_level),
          formatArea(unit.habitable_area_m2),
          formatArea(unit.terrace_area_m2),
          formatArea(unit.garden_area_m2),
          formatMoney(prices.commercial_land_price),
          formatMoney(prices.commercial_construction_price),
          formatMoney(calculateCommercialTotal(prices)),
        ]

        if (index % 2 === 1) {
          doc.setFillColor(250, 250, 250)
          doc.rect(margin, y - 5.5, pageWidth - (margin * 2), rowHeight, 'F')
        }

        doc.setFont('helvetica', 'normal')
        doc.setFontSize(7.5)
        let x = margin + 1
        row.forEach((value, colIndex) => {
          const maxWidth = colWidths[colIndex] - 2
          const text = doc.splitTextToSize(value, maxWidth)[0] || ''
          doc.text(text, x, y)
          x += colWidths[colIndex]
        })
        doc.setDrawColor(235, 235, 235)
        doc.line(margin, y + 2.5, pageWidth - margin, y + 2.5)
        y += rowHeight
      })

      addFooter()
      doc.save(`commercial_prijslijst_${projectName.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}.pdf`)
      toast.success('Commercial PDF generated')
    } catch (error) {
      console.error('Error generating commercial PDF:', error)
      toast.error('Failed to generate commercial PDF')
    } finally {
      setExportingCommercialPdf(false)
    }
  }

  const fetchProjectStatuses = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/projects/statuses`)
    setStatuses(response.data)
  }

  const fetchProject = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/projects/get/${projectID}`)
    const data = response.data
    setProject(data)

    setLoading(false)
  }

  const fetchTags = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/tags/get/${projectID}?type=project`)
    setTags(response.data)
  }

  const fetchNotes = async () => {
    let id = null
    if (selectedType == 'project') {
      id = projectID
    } else {
      id = selectedItem.id
    }

    if (!id) {
      return
    }

    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/notes/get/${id}?type=${selectedType}`)
    if (response.status == 200) {
      setNotes(response.data)
    }

  }

  const fetchFullContacts = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + "/api/contacts/get")
    setContactsList(response.data)
  }

  useEffect(() => {
    fetchProject()
    fetchProjectStatuses()
    fetchTags()
    fetchNotes()
    fetchFullContacts()
  }, [])

  useEffect(() => {
    fetchNotes()
  }, [selectedItem, selectedType])

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleEditButton = () => {
    if (edit) {
      setEdit(false)
    } else {
      setEdit(true)
    }
  }

  const handleOnClickStatus = async (e) => {
    if (!edit) {
      return
    }

    setloadingStatus(true)

    const statusId = e.currentTarget.getAttribute('data-status-id')
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/project/change-status?projectID=${projectID}&statusID=${statusId}`)
    console.log(response.data)

    fetchProject()
      .then(() => {
        setloadingStatus(false)
      })
  }

  const handleAdd = (e, type, data = null) => {
    e.stopPropagation()

    setSelectedItemID(null)
    setSelectedType(type)
    setActionType("add")

    if (type == "building") {
      setBuildingDetailIsActive(true)
      if (data) {
        setSelectedItem(data)
      }
    } else if (type == "unit" && data?.building_id) {
      // Navigate to new unit page
      navigate(`/projects/${projectID}/buildings/${data.building_id}/units/new`)
    }
  }

  const handleClickItem = (e, type, itemId, buildingId = null) => {
    e.stopPropagation()

    switch (type) {
      case 'building':
        const building = project.buildings.find(building => building.id == itemId)
        setBuildingDetailIsActive(true)
        setSelectedItem(building)
        setSelectedItemID(itemId)
        setSelectedType(type)
        setActionType("edit")
        break
      case 'unit':
        // Navigate to unit detail page
        navigate(`/projects/${projectID}/buildings/${buildingId}/units/${itemId}`)
        break
    }
  }

  const handleDelete = async (e, type, itemId, itemName) => {
    e.stopPropagation()

    // Show custom confirmation popup
    showNotificationPopup({
      message: `Are you sure you want to delete this ${type}? ${itemName}`,
      onAction: async () => {
        try {
          let endpoint = ''
          if (type === 'building') {
            endpoint = `${import.meta.env.VITE_API_URL}/api/buildings/${itemId}`
          } else if (type === 'unit') {
            endpoint = `${import.meta.env.VITE_API_URL}/api/units/${itemId}`
          } else if (type === 'project') {
            endpoint = `${import.meta.env.VITE_API_URL}/api/projects/${itemId}`
          }

          const response = await httpClient.delete(endpoint)
          
          if (response.status === 200) {
            toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`)
            
            if (type === 'project') {
              // Navigate back to projects list after successful project deletion
              navigate('/projects')
            } else {
              fetchProject() // Refresh the project data for buildings/units
            }
          }
          if (response.status === 400) {
            toast.error(`Cannot delete ${type} - it may have dependent records`)
          }
          if (response.status === 404) {
            toast.error(`${type.charAt(0).toUpperCase() + type.slice(1)} not found`)
          }
        } catch (error) {
          console.error(`Error deleting ${type}:`, error)
          if (error.response && error.response.data && error.response.data.error) {
            toast.error(error.response.data.error)
          } else {
            toast.error(`Failed to delete ${type}`)
          }
        }
      },
      onClose: () => {
        // User cancelled, do nothing
      },
      cancelButton: {
        text: "Cancel",
        color: "var(--secondairy-light-medium-gray)"
      },
      actionButton: {
        text: "Delete",
        color: "var(--dark-red)"
      }
    })
  }

  const isInactiveUnit = (unit) => {
    const normalizedStatus = unit?.status?.toLowerCase()

    return normalizedStatus === 'inactive' || normalizedStatus === 'demolished'
  }

  const hasProjectStatus = (statusId) => {
    if (Array.isArray(project.statuses)) {
      return project.statuses.some(projectStatus => projectStatus.id == statusId)
    }

    return project.status_id?.id == statusId || project.status_id == statusId
  }

  return (
    <div className='template'>

      <Header>
        {
          !popupIsActive &&
          <>
            <div className="project-detail-header-left">
              <div className="back_button" onClick={handleGoBack}><KeyboardBackspaceIcon /> Go Back</div>
            </div>

            <div className="header__actions--left project-detail-header-actions">
              <ImportButton
                endpoint={`${import.meta.env.VITE_API_URL}/api/units/import`}
                additionalData={{ project_id: projectID }}
                onImportSuccess={(data) => {
                  toast.success(`Import successful! ${data.message}`)
                  fetchProject() // Refresh the project data
                }}
                onImportError={(error) => {
                  toast.error(`Something went wrong 😢`)
                }}
                disabled={loading || !project.id}
              />
              <ExportButton
                data={prepareExportData}
                filename={`units_${project.project_commercial_name || project.project_placeholder_name || 'export'}`}
                formats={['excel']}
                disabled={loading || !project.id}
              />
              <button
                className="export-button"
                onClick={handleCommercialPdfExport}
                disabled={exportingCommercialPdf || loading || !project.id}
              >
                {exportingCommercialPdf ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <>
                    <DownloadIcon style={{ marginRight: '4px', fontSize: '14px' }} />
                    Commercial PDF
                  </>
                )}
              </button>
            </div>

            <div className="header__options--right project-detail-header-options">
              <div className="hide-inactive-filter">
                <Slider
                  text="Hide inactive"
                  defaultState={hideInactive}
                  state={hideInactive}
                  size={1.2}
                  fontSize="0.8rem"
                  onClickFunction={(id, newState) => setHideInactive(newState)}
                />
              </div>
              {
                edit ?
                  <Button variant='primary' tone='red' size='sm' id="project__btn-cancel" onClick={handleEditButton}>Close Edit</Button> :
                  <Button variant='primary' size='sm' id="project__btn-edit" onClick={handleEditButton}>Edit</Button>
              }
            </div>
          </>
        }
      </Header>

      <MenuBar />

      <PopupWindowProjectDetail
        isActive={projectDetailIsActive}
        setIsActive={setProjectDetailIsActive}
        projectID={projectID}
        project={project}
        setProject={setProject}
        fetchProject={fetchProject}
        contactsList={contactsList}
      />

      <PopupWindowBuildingDetail
        isActive={buildingDetailIsActive}
        setIsActive={setBuildingDetailIsActive}
        actionType={actionType}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        projectID={projectID}
        selectedItemID={selectedItemID}
        fetchProject={fetchProject}
        contactsList={contactsList}
      />


      <div className="template__body" id='body-project'>

        <div className='status_bar' id='status-bar'>
          {
            statuses &&
            statuses.map((status, index) => (
              <div className={`status ${hasProjectStatus(status.id) ? `active` : ""} ${status.label_color} ${edit ? "editable" : ""}`} key={index} data-index={index} data-status-id={status.id} onClick={handleOnClickStatus}>
                {
                  loadingStatus ?
                    <div className='loading_spinner--circle'><CircularProgress color="inherit" /></div> :
                    <>{status.status_name}</>
                }
              </div>
            ))
          }
        </div>

        <div className='project_detail_wrapper'>
          {
            loading ?
              <LoadingSpinner /> :
              <>
                <div 
                  className='body__section project' 
                  id='project-detail-project' 
                  onClick={(e) => setProjectDetailIsActive(true)}
                  onMouseEnter={() => edit && setShowDeleteButton({ type: 'project', id: project.id })}
                  onMouseLeave={() => setShowDeleteButton({ type: null, id: null })}
                >
                  {
                    project.project_commercial_name ?
                      <div>
                        <p>{project.project_commercial_name}</p>
                        <p>{project.project_placeholder_name}</p>
                      </div>
                      :
                      <div>
                        <p>{project.project_placeholder_name}</p>
                      </div>
                  }
                  {
                    edit && showDeleteButton.type === 'project' && showDeleteButton.id === project.id && (
                      <Button 
                        variant='icon'
                        tone='red'
                        iconOnly
                        className="delete-button"
                        onClick={(e) => handleDelete(e, "project", project.id, project.project_commercial_name || project.project_placeholder_name)}
                        title="Delete project"
                        aria-label='Delete project'
                      >
                        <DeleteOutlineIcon />
                      </Button>
                    )
                  }
                </div>

                <section className='section__buildings'>
                  {
                    project.buildings &&
                    project.buildings
                      .map((building) => {
                        const sortedUnits = [...(building.units || [])].sort((a, b) => {
                          const aUnitName = a.unit_name || ''
                          const bUnitName = b.unit_name || ''
                          const aMatch = aUnitName.match(/(\d+)$/)
                          const bMatch = bUnitName.match(/(\d+)$/)

                          if (aMatch && bMatch) {
                            return parseInt(aMatch[1]) - parseInt(bMatch[1])
                          }

                          return aUnitName.localeCompare(bUnitName)
                        })

                        const allUnitsInactive = sortedUnits.length > 0 && sortedUnits.every(isInactiveUnit)
                        const visibleUnits = hideInactive
                          ? sortedUnits.filter((unit) => !isInactiveUnit(unit))
                          : sortedUnits

                        if (hideInactive && visibleUnits.length === 0) {
                          return null
                        }

                        return (
                      <div className="building" id={building.id} key={building.id} onClick={(e) => handleClickItem(e, "building", building.id)} >
                        <div 
                          className={`body__section project ${allUnitsInactive ? 'all-units-inactive' : ''}`}
                          onMouseEnter={() => edit && setShowDeleteButton({ type: 'building', id: building.id })}
                          onMouseLeave={() => setShowDeleteButton({ type: null, id: null })}
                        >
                          <div className='icon'><BuildingIcon /></div>
                          <p>{building.building_name}</p>
                          {
                            edit && showDeleteButton.type === 'building' && showDeleteButton.id === building.id && (
                              <Button 
                                variant='icon'
                                tone='red'
                                iconOnly
                                className="delete-button"
                                onClick={(e) => handleDelete(e, "building", building.id, building.building_name)}
                                title="Delete building"
                                aria-label='Delete building'
                              >
                                <DeleteOutlineIcon />
                              </Button>
                            )
                          }
                        </div>
                        <section className='section__units' onClick={(e) => e.stopPropagation()}>
                          {
                            visibleUnits
                              .map((unit) => {
                                const unitStatusClass = unit.status?.toLowerCase() || 'available'

                                 return (
                                    <div
                                     className={`unit ${unitStatusClass}`}
                                     key={unit.id}
                                     onClick={(e) => {
                                       handleClickItem(e, "unit", unit.id, building.id)
                                     }}
                                    onMouseEnter={() => edit && setShowDeleteButton({ type: 'unit', id: unit.id })}
                                    onMouseLeave={() => setShowDeleteButton({ type: null, id: null })}
                                  >
                                    <div className='unit-name'>
                                      {unit.unit_name}
                                    </div>
                                    <div className='unit-info'>
                                      <div className='unit-info-type'>{unit.type}</div>
                                    </div>
                                    {
                                      edit && showDeleteButton.type === 'unit' && showDeleteButton.id === unit.id && (
                                        <Button 
                                          variant='icon'
                                          tone='red'
                                          iconOnly
                                          className="delete-button"
                                          onClick={(e) => handleDelete(e, "unit", unit.id, unit.unit_name)}
                                          title="Delete unit"
                                          aria-label='Delete unit'
                                        >
                                          <DeleteOutlineIcon />
                                        </Button>
                                      )
                                    }
                                  </div>
                                )
                              })
                          }

                          {
                            edit ?
                              <div className="unit new-button" onClick={(e) => handleAdd(e, "unit", { "building_id": building.id })}>add unit</div> : null
                          }
                        </section>

                       </div>
                        )
                      })
                  }

                  {
                    edit ?

                      <div className="building">
                        <div className='body__section new-button' onClick={(e) => handleAdd(e, "building")}>
                          <p>add building</p>
                        </div>
                      </div> : null
                  }

                </section>
              </>
          }
        </div>

      </div>

    </div >
  )
}

export default ProjectDetailPage
